import { EventEmitter } from 'node:events';
import Bonjour, { type Browser, type Service } from 'bonjour-service';

const SERVICE_TYPE = 'zaparoo';
const DEFAULT_PORT = 7497;

export interface DiscoveredDevice {
  id: string;
  name?: string;
  host: string;
  port: number;
  txtRecord: {
    id?: string;
    version?: string;
    platform?: string;
  };
}

export interface MdnsDiscoveryEvents {
  discovered: [device: DiscoveredDevice];
  removed: [deviceId: string];
}

type BrowserInstance = InstanceType<typeof Browser>;
type ServiceInstance = InstanceType<typeof Service>;

export class MdnsDiscovery extends EventEmitter<MdnsDiscoveryEvents> {
  private bonjour: InstanceType<typeof Bonjour> | null = null;
  private browser: BrowserInstance | null = null;
  private knownDevices = new Set<string>();

  start(): void {
    if (this.bonjour) return;

    this.bonjour = new Bonjour();
    this.browser = this.bonjour.find({ type: SERVICE_TYPE, protocol: 'tcp' }, (service) =>
      this.onServiceUp(service),
    );
    this.browser.on('down', (service: ServiceInstance) => this.onServiceDown(service));
  }

  stop(): void {
    this.browser?.stop();
    this.browser = null;
    this.bonjour?.destroy();
    this.bonjour = null;
    this.knownDevices.clear();
  }

  private resolveHost(service: ServiceInstance): string {
    if (service.addresses && service.addresses.length > 0) {
      return service.addresses[0];
    }
    return service.host;
  }

  private deviceEndpoint(service: ServiceInstance): { host: string; port: number; id: string } {
    const host = this.resolveHost(service);
    const port = service.port || DEFAULT_PORT;
    return { host, port, id: `${host.includes(':') ? `[${host}]` : host}:${port}` };
  }

  private onServiceUp(service: ServiceInstance): void {
    const { host, port, id } = this.deviceEndpoint(service);

    if (this.knownDevices.has(id)) return;
    this.knownDevices.add(id);

    const txt = (service.txt ?? {}) as Record<string, string>;
    this.emit('discovered', {
      id,
      ...(service.name ? { name: service.name } : {}),
      host,
      port,
      txtRecord: {
        id: txt.id,
        version: txt.version,
        platform: txt.platform,
      },
    });
  }

  private onServiceDown(service: ServiceInstance): void {
    const { id } = this.deviceEndpoint(service);

    if (!this.knownDevices.has(id)) return;
    this.knownDevices.delete(id);

    this.emit('removed', id);
  }
}
